import type { Character } from '@guildrise/shared';
import type { CollectionState } from '../collection/use-collection.js';
import { ProfileIcon, type ProfileIconName } from './profile-icon.js';
import { collectionSummary } from './profile-view.js';

const SLOT_KEYS = ['slot-1', 'slot-2', 'slot-3', 'slot-4', 'slot-5'];

export function ProfileBlocks({
  catalog,
  collection,
}: {
  readonly catalog: readonly Character[];
  readonly collection: CollectionState;
}): React.ReactElement {
  const summary =
    collection.status === 'ready'
      ? collectionSummary(
          catalog,
          collection.collection.characters.map(({ characterId }) => characterId),
        )
      : null;
  const collectionLabel = summary
    ? `${summary.owned}\u00a0из\u00a0${summary.total}`
    : collection.status === 'loading'
      ? 'Загрузка…'
      : 'Недоступна';
  return (
    <div className="profile-blocks">
      <section className="profile-power" aria-label="Боевая мощь">
        <div className="profile-power__crest">
          <img
            className="profile-power__art"
            src="/art/profile/power-celestial-glass-v1-960.webp"
            srcSet="/art/profile/power-celestial-glass-v1-480.webp 480w, /art/profile/power-celestial-glass-v1-960.webp 960w"
            sizes="(min-width: 900px) 36vw, calc(100vw - 32px)"
            width={960}
            height={644}
            alt=""
            decoding="async"
          />
          <div className="profile-power__copy">
            <h2>Боевая мощь</h2>
            <strong className="profile-power__value" title="Пока недоступна">
              —
            </strong>
          </div>
        </div>
      </section>
      <Panel icon="users-three" title="Стенд героев">
        <ul className="profile-showcase" aria-label="Стенд пока недоступен">
          {SLOT_KEYS.map((key) => (
            <li
              key={key}
              className="profile-showcase__slot profile-showcase__slot--empty"
              aria-label="Пустое место"
            />
          ))}
        </ul>
      </Panel>
      <Panel icon="chart-bar" title="Статистика">
        <dl className="profile-stats">
          <Stat icon="users-three" label="Коллекция" value={collectionLabel} />
          <Stat icon="compass" label="Бездна" value="—" />
          <Stat icon="sword" label="Бои на арене" value="—">
            <dd className="profile-stats__row">
              <span>Побед подряд</span>
              <span>—</span>
            </dd>
          </Stat>
          <Stat icon="medal" label="Достижения" value="—" />
        </dl>
        <p className="profile-panel__note">Статистика пока недоступна, кроме коллекции.</p>
      </Panel>
      <Panel icon="medal" title="Медальки">
        <ul className="profile-medals" aria-label="Пустая витрина медалек">
          {SLOT_KEYS.map((key) => (
            <li key={key} className="profile-medals__socket" aria-hidden="true" />
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function Panel({
  icon,
  title,
  children,
}: {
  readonly icon: ProfileIconName;
  readonly title: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="profile-panel" aria-label={title}>
      <header className="profile-panel__head">
        <span className="profile-panel__icon">
          <ProfileIcon name={icon} />
        </span>
        <h2>{title}</h2>
      </header>
      {children}
    </section>
  );
}

function Stat({
  icon,
  label,
  value,
  children,
}: {
  readonly icon: ProfileIconName;
  readonly label: string;
  readonly value: string;
  readonly children?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="profile-stats__tile">
      <dt>
        <ProfileIcon name={icon} size={16} />
        {label}
      </dt>
      <dd className="profile-stats__value">{value}</dd>
      {children}
    </div>
  );
}
